const DEFAULT_APP_URLS = {
    consumer: 'http://localhost:3000',
    studio: 'http://localhost:3001',
    agency: 'http://localhost:3002',
    partners: 'http://localhost:3003',
    admin: 'http://localhost:3004',
    create: 'http://localhost:3005',
};
const ENV_KEYS = {
    consumer: 'VITE_CONSUMER_URL',
    studio: 'VITE_STUDIO_URL',
    agency: 'VITE_AGENCY_URL',
    partners: 'VITE_PARTNERS_URL',
    admin: 'VITE_ADMIN_URL',
    create: 'VITE_CREATE_URL',
};
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const SOFT_POC_CONSUMER = 'https://fetmode-web.pages.dev';
const SOFT_POC_STUDIO = 'https://fetmode-studio.pages.dev';
const SOFT_POC_ADMIN = 'https://admin.fetmode-web.pages.dev';
const SOFT_POC_API = 'https://fetmode-api.fetmode.workers.dev';
/** Soft POC siblings — undeployed agency/partners/create point at consumer (no localhost ports). */
const SOFT_POC_APP_URLS = {
    consumer: SOFT_POC_CONSUMER,
    studio: SOFT_POC_STUDIO,
    admin: SOFT_POC_ADMIN,
    agency: SOFT_POC_CONSUMER,
    partners: SOFT_POC_CONSUMER,
    create: SOFT_POC_CONSUMER,
};
const BRAND_APP_URLS = {
    consumer: 'https://fetmode.com',
    studio: 'https://studio.fetmode.com',
    agency: 'https://agency.fetmode.com',
    partners: 'https://partners.fetmode.com',
    admin: 'https://admin.fetmode.com',
    create: 'https://create.fetmode.com',
};
/** Soft POC + brand hosts → sibling app bases (no ports). */
const DEPLOYED_APP_URLS_BY_HOST = {
    'fetmode-web.pages.dev': SOFT_POC_APP_URLS,
    'fetmode-studio.pages.dev': SOFT_POC_APP_URLS,
    'admin.fetmode-web.pages.dev': SOFT_POC_APP_URLS,
    'fetmode.com': BRAND_APP_URLS,
    'www.fetmode.com': BRAND_APP_URLS,
    'studio.fetmode.com': BRAND_APP_URLS,
    'agency.fetmode.com': BRAND_APP_URLS,
    'partners.fetmode.com': BRAND_APP_URLS,
    'admin.fetmode.com': BRAND_APP_URLS,
    'create.fetmode.com': BRAND_APP_URLS,
};
const DEPLOYED_API_URLS_BY_HOST = {
    'fetmode-web.pages.dev': SOFT_POC_API,
    'fetmode-studio.pages.dev': SOFT_POC_API,
    'admin.fetmode-web.pages.dev': SOFT_POC_API,
    'fetmode.com': 'https://api.fetmode.com',
    'www.fetmode.com': 'https://api.fetmode.com',
    'studio.fetmode.com': 'https://api.fetmode.com',
    'agency.fetmode.com': 'https://api.fetmode.com',
    'partners.fetmode.com': 'https://api.fetmode.com',
    'admin.fetmode.com': 'https://api.fetmode.com',
    'create.fetmode.com': 'https://api.fetmode.com',
};
function isLanDevHostname(hostname) {
    if (LOOPBACK_HOSTS.has(hostname)) {
        return false;
    }
    if (hostname.endsWith('.ts.net') || hostname.endsWith('.local')) {
        return true;
    }
    // RFC1918 + Tailscale CGNAT (100.64.0.0/10)
    return /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.|100\.(6[4-9]|[7-9]\d|1[0-1]\d)\.)/.test(hostname);
}
/** Map Pages preview hosts (hash.project.pages.dev) to the production project map. */
function canonicalizeDeployedHostname(hostname) {
    if (DEPLOYED_APP_URLS_BY_HOST[hostname]) {
        return hostname;
    }
    if (hostname.endsWith('.fetmode-studio.pages.dev')) {
        return 'fetmode-studio.pages.dev';
    }
    if (hostname.endsWith('.fetmode-web.pages.dev')) {
        return 'fetmode-web.pages.dev';
    }
    return undefined;
}
/**
 * When the page is opened via a LAN IP/hostname, rewrite loopback app URLs
 * to that same host so cross-app links stay reachable.
 * Never used on Pages / brand hosts (those use DEPLOYED_APP_URLS_BY_HOST).
 */
export function rewriteLoopbackAppUrl(url, pageHostname = typeof window !== 'undefined' ? window.location.hostname : undefined) {
    if (!pageHostname || !isLanDevHostname(pageHostname)) {
        return url;
    }
    try {
        const parsed = new URL(url);
        if (!LOOPBACK_HOSTS.has(parsed.hostname)) {
            return url;
        }
        parsed.hostname = pageHostname;
        const port = parsed.port ? `:${parsed.port}` : '';
        const path = parsed.pathname === '/' ? '' : parsed.pathname;
        return `${parsed.protocol}//${parsed.hostname}${port}${path}${parsed.search}${parsed.hash}`;
    }
    catch {
        return url;
    }
}
function deployedUrlForApp(app, pageHostname) {
    if (!pageHostname) {
        return undefined;
    }
    const canonical = canonicalizeDeployedHostname(pageHostname);
    if (!canonical) {
        return undefined;
    }
    return DEPLOYED_APP_URLS_BY_HOST[canonical]?.[app];
}
function deployedApiUrl(pageHostname) {
    if (!pageHostname) {
        return undefined;
    }
    const canonical = canonicalizeDeployedHostname(pageHostname);
    if (!canonical) {
        return undefined;
    }
    return DEPLOYED_API_URLS_BY_HOST[canonical];
}
export function resolveAppUrl(app, env = {}, pageHostname = typeof window !== 'undefined' ? window.location.hostname : undefined) {
    const configured = env[ENV_KEYS[app]]?.trim();
    if (configured && configured.length > 0) {
        try {
            const host = new URL(configured).hostname;
            if (LOOPBACK_HOSTS.has(host)) {
                const deployedFromLoopbackEnv = deployedUrlForApp(app, pageHostname);
                if (deployedFromLoopbackEnv) {
                    return deployedFromLoopbackEnv;
                }
                return rewriteLoopbackAppUrl(configured, pageHostname);
            }
            return configured.replace(/\/$/, '');
        }
        catch {
            return configured;
        }
    }
    const deployed = deployedUrlForApp(app, pageHostname);
    if (deployed) {
        return deployed;
    }
    // Known deployed host but missing map entry — never emit localhost ports.
    const canonical = pageHostname ? canonicalizeDeployedHostname(pageHostname) : undefined;
    if (canonical) {
        const map = DEPLOYED_APP_URLS_BY_HOST[canonical];
        if (map) {
            return map.consumer;
        }
    }
    return rewriteLoopbackAppUrl(DEFAULT_APP_URLS[app], pageHostname);
}
/**
 * API base for the current page host. Prefer `VITE_API_URL` when it is a real
 * (non-loopback) URL; on Pages/brand hosts fall back to the soft-POC/brand API
 * instead of relative `/api` (which only exists in local Vite).
 */
export function resolveApiUrl(env = {}, pageHostname = typeof window !== 'undefined' ? window.location.hostname : undefined, fallback = '/api') {
    const configured = env.VITE_API_URL?.trim();
    if (configured && configured.length > 0) {
        try {
            const host = new URL(configured).hostname;
            if (LOOPBACK_HOSTS.has(host)) {
                const deployed = deployedApiUrl(pageHostname);
                if (deployed) {
                    return deployed;
                }
                return rewriteLoopbackAppUrl(configured, pageHostname);
            }
            return configured.replace(/\/$/, '');
        }
        catch {
            // Relative path like `/api`
            if (configured.startsWith('/')) {
                const deployed = deployedApiUrl(pageHostname);
                if (deployed) {
                    return deployed;
                }
                return configured;
            }
            return configured;
        }
    }
    const deployed = deployedApiUrl(pageHostname);
    if (deployed) {
        return deployed;
    }
    return fallback;
}
export function resolveAppUrls(env = {}, pageHostname) {
    return {
        consumer: resolveAppUrl('consumer', env, pageHostname),
        studio: resolveAppUrl('studio', env, pageHostname),
        agency: resolveAppUrl('agency', env, pageHostname),
        partners: resolveAppUrl('partners', env, pageHostname),
        admin: resolveAppUrl('admin', env, pageHostname),
        create: resolveAppUrl('create', env, pageHostname),
    };
}
//# sourceMappingURL=urls.js.map